import type { ChangeEvent, InputHTMLAttributes, ReactNode, Ref } from 'react';

import { cn } from '@shared/lib/cn';
import { Icon } from '@shared/ui/icon';

import { type CheckboxSize, type CheckboxStatus, LABEL_SIZE_CLASS } from './checkbox.constant';
import { CheckboxSkeleton } from './checkbox.skeleton.ui';

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size' | 'children'
> {
  /** Figma `size`: 라벨 글자 md 14 · sm 12. 기본 md */
  size?: CheckboxSize;
  /** Figma `checked`(제어). 비제어는 `defaultChecked` */
  checked?: boolean;
  defaultChecked?: boolean;
  /** 체크가 바뀔 때 새 값 */
  onCheckedChange?: (checked: boolean) => void;
  /** Figma `status`. init 이면 스켈레톤 */
  status?: CheckboxStatus;
  /** Figma `Label`(+ `Show label`). 없으면 상자만 그린다 — 그때는 `aria-label` 을 준다 */
  children?: ReactNode;
  /** 바깥 `<label>` 에 붙는다 */
  className?: string;
  ref?: Ref<HTMLInputElement>;
}

/** 체크박스. 상자는 네이티브 `<input type="checkbox">` 에 모양을 입힌 것이다 */
export function Checkbox({
  size = 'md',
  status = 'default',
  children,
  className,
  disabled,
  onChange,
  onCheckedChange,
  ref,
  ...rest
}: CheckboxProps) {
  if (status === 'init') return <CheckboxSkeleton className={className} />;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(event);
    onCheckedChange?.(event.target.checked);
  };

  return (
    <label
      data-slot="checkbox"
      data-size={size}
      className={cn(
        'inline-flex items-center gap-1 align-middle',
        disabled ? 'cursor-not-allowed text-fg-disabled' : 'cursor-pointer text-fg-default',
        className,
      )}
    >
      <span className="relative inline-flex size-4 shrink-0 items-center justify-center">
        <input
          ref={ref}
          type="checkbox"
          {...rest}
          disabled={disabled}
          onChange={handleChange}
          className="peer absolute inset-0 m-0 size-4 cursor-pointer disabled:cursor-not-allowed appearance-none rounded-sm border border-border-control bg-bg-surface checked:border-transparent checked:bg-bg-primary disabled:border-border-default disabled:bg-bg-disabled disabled:checked:border-transparent"
        />
        {/* 체크 표시 — 흰색(`fg.inverse`, DD-25. primary 면 대비 2.17:1 은 사용자 결정). Figma 는 14×14 아이콘 자리, 가장 가까운 토큰 sm(16) */}
        <Icon
          name="check"
          size="sm"
          className="pointer-events-none relative invisible text-fg-inverse peer-checked:visible peer-disabled:text-fg-disabled"
        />
      </span>
      {children ? <span className={LABEL_SIZE_CLASS[size]}>{children}</span> : null}
    </label>
  );
}
